import { useCheckYoutubeChannelApiV1, useVerifyYoutubeChannelApiV1 } from "@/api/__generated__/api.schema";
import { ExternalLink } from "@/components/external-link";
import { YOUTUBE_CHANNEL_VERIFICATION_CODE } from "@/constants/code";
import { externalUrls, getAssetsUrl } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { NewTrackingEvent, TrackingEvent, trackEvent, trackNewEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { RegisterMethodRadio } from "@/routes/channels/components/register/register-method-radio";
import { useRegisterChannelViaGoogleOAuth } from "@/routes/channels/hooks/use-register-channel";
import { getMobileBrowserInfo, isIOS } from "@/utils/platform";
import { useToastMessage } from "@backpac/monica";
import {
  Alert,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FullScreenOverlayLayout,
  Icon,
  Input,
  Label,
  Step,
  StepGroup,
  StepNext,
  StepPrevious,
  Typography,
  useStep,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { z } from "zod";

interface RegisterChannelFlowProps {
  defaultChannel?: { url: string; id: string };
  defaultStep?: number;
  onClose?: () => void;
  onSuccess: (channelId: string) => void;
}

export function RegisterChannelFlow({ onClose, onSuccess, defaultStep, defaultChannel }: RegisterChannelFlowProps) {
  const [method, setMethod] = useState<"google" | "manual">("google");
  const [channel, setChannel] = useState<{ url: string; id: string }>(defaultChannel ?? { url: "", id: "" });

  return (
    <StepGroup defaultStep={defaultStep}>
      <Step value={1}>
        <RegisterChannelFlow.GridLayout>
          <SelectRegisterMethod value={method} onChange={setMethod} onClose={onClose} />
          <RegisterChannelFlow.Banner name="step_1" />
        </RegisterChannelFlow.GridLayout>
      </Step>
      <Step value={2}>
        <RegisterChannelFlow.GridLayout>
          <RegisterChannelUrlForm onSubmitSuccess={setChannel} defaultValue={channel?.url} />
          <RegisterChannelFlow.Banner name="step_2" />
        </RegisterChannelFlow.GridLayout>
      </Step>
      <Step value={3}>
        <RegisterChannelFlow.GridLayout>
          <VerifyChannel channel={channel} onSuccess={onSuccess} />
          <RegisterChannelFlow.Banner name="step_3" />
        </RegisterChannelFlow.GridLayout>
      </Step>
    </StepGroup>
  );
}

RegisterChannelFlow.GridLayout = function GridLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <RegisterChannelFlow.MobileNavigation />
      <div className="grid grid-cols-1 auto-rows-min md:grid-cols-2 pb-28 overflow-y-scroll h-[100dvh] md:h-full md:pb-0">
        <div className="order-2 md:order-1">{Array.isArray(children) ? children[0] : null}</div>
        <div className="order-1 md:order-2">{Array.isArray(children) ? children[1] : null}</div>
      </div>
    </>
  );
};

RegisterChannelFlow.Title = function Title({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Typography size="lg" font="unbounded" className={className}>
      {children}
    </Typography>
  );
};

RegisterChannelFlow.Content = function Content({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col p-6 md:p-12 h-full bg-white justify-between md:rounded-l-[12px]">{children}</div>
  );
};

RegisterChannelFlow.Banner = function Banner({ name }: { name: string }) {
  const isMobile = useIsMobile();

  return (
    <img
      fetchPriority="high"
      className="rounded-none md:rounded-r-[12px] min-h-[220px] 2xl:min-h-[520px]"
      src={getAssetsUrl(`verification_${name}_${isMobile ? "mobile" : "desktop"}.png`)}
      alt=""
    />
  );
};

RegisterChannelFlow.BackButton = function BackButton({ className }: { className?: string }) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return null;
  }

  return (
    <StepPrevious>
      <div className={cn("flex items-center gap-[6px]", className)}>
        <i className="inline-block i-bp-chevron-left size-3" />
        <Typography size="xs" weight={600}>
          Back
        </Typography>
      </div>
    </StepPrevious>
  );
};

RegisterChannelFlow.MobileNavigation = function MobileNavigation() {
  const { activeStep } = useStep();
  const isMobile = useIsMobile();

  if (!isMobile) {
    return null;
  }

  return (
    <div className="flex items-center gap-[6px] justify-between h-[64px] px-4">
      {activeStep === 1 ? (
        <div />
      ) : (
        <StepPrevious asChild>
          <button type="button">
            <i className="inline-block i-bp-arrow-left size-4" />
            <span className="sr-only">Back</span>
          </button>
        </StepPrevious>
      )}
      <FullScreenOverlayLayout.Close />
    </div>
  );
};

RegisterChannelFlow.CTA = function CTA({ className, ...props }: React.ComponentProps<typeof Button>) {
  const browser = getMobileBrowserInfo();

  return (
    <div
      className={cn("px-4 desktop:px-0 fixed bottom-4 left-0 right-0 desktop:static", {
        // NOTE: workaround for iOS fixed button position issue
        "bottom-24": isIOS() && browser.isSafari,
        "bottom-[120px]": isIOS() && browser.isChrome,
      })}
    >
      <Button size="lg" className={cn("w-full", className)} {...props} />
    </div>
  );
};

interface SelectRegisterMethodProps {
  value: "google" | "manual";
  onChange: (option: "google" | "manual") => void;
  onClose?: () => void;
}

function SelectRegisterMethod({ value, onChange, onClose }: SelectRegisterMethodProps) {
  const registerYoutubeChannel = useRegisterChannelViaGoogleOAuth({
    onSuccess: (channelId) => {
      trackEvent(TrackingEvent.ChannelAdded, {
        channel_id: channelId,
        is_success: true,
      });
      trackNewEvent(NewTrackingEvent.CompleteChannelVerification, {
        channel_handle: channelId,
        register_options: "google account",
      });
      onClose?.();
    },
  });

  return (
    <RegisterChannelFlow.Content>
      <div className="space-y-6 mb-8">
        <RegisterChannelFlow.Title>How do you want to add your channel?</RegisterChannelFlow.Title>
        <RegisterMethodRadio value={value} onValueChange={onChange} />
      </div>
      {value === "manual" ? (
        <StepNext asChild>
          <RegisterChannelFlow.CTA
            onClick={() => {
              trackNewEvent(NewTrackingEvent.ClickContinueButtonOfAddOptionModal, {
                register_options: "channel URL",
              });
            }}
          >
            Continue
          </RegisterChannelFlow.CTA>
        </StepNext>
      ) : (
        <RegisterChannelFlow.CTA
          onClick={() => {
            trackNewEvent(NewTrackingEvent.ClickContinueButtonOfAddOptionModal, {
              register_options: "google account",
            });
            registerYoutubeChannel();
          }}
        >
          Continue
        </RegisterChannelFlow.CTA>
      )}
    </RegisterChannelFlow.Content>
  );
}

const youtubeChannelFormScheme = z.object({
  url: z.string().refine(
    (url) => {
      return url.includes("youtube.com") || url.includes("@");
    },
    {
      message: "Invalid URL",
    },
  ),
});

interface RegisterChannelUrlFormProps {
  defaultValue?: string;
  onSubmitSuccess: (channel: { url: string; id: string }) => void;
}

function RegisterChannelUrlForm({ onSubmitSuccess, defaultValue }: RegisterChannelUrlFormProps) {
  const { next } = useStep();
  const workspaceId = useWorkspaceId();
  const form = useForm<z.infer<typeof youtubeChannelFormScheme>>({
    defaultValues: {
      url: defaultValue ?? "",
    },
    resolver: zodResolver(youtubeChannelFormScheme),
  });
  const {
    mutateAsync: checkYoutubeChannel,
    isPending,
    isError,
    error,
  } = useCheckYoutubeChannelApiV1({
    mutation: {
      onSuccess: (data) => {
        if (data.data != null) {
          onSubmitSuccess({ url: data.data.url, id: data.data.channelId });
          next();
        }
      },
    },
  });

  const onSubmit = useCallback(
    async (data: z.infer<typeof youtubeChannelFormScheme>) => {
      const transformedUrl = data.url.startsWith("@") ? `youtube.com/${data.url}` : data.url;
      await checkYoutubeChannel({ data: { url: transformedUrl }, workspaceId });
    },
    [checkYoutubeChannel, workspaceId],
  );

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewEnterURLModal, {});
  }, []);

  return (
    <RegisterChannelFlow.Content>
      <Form {...form}>
        <form id="register-channel-url-form" className="h-full space-y-6 mb-6" onSubmit={form.handleSubmit(onSubmit)}>
          <RegisterChannelFlow.BackButton />
          <RegisterChannelFlow.Title>Enter your YouTube Channel URL</RegisterChannelFlow.Title>
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Channel URL</FormLabel>
                <Input placeholder="Channel URL or handle" {...field} />
                {form.formState.errors.url ? (
                  <FormMessage />
                ) : form.formState.isDirty ? (
                  <Typography size="xs" color="textMuted">
                    e.g. youtube.com/@backpac or @handle
                  </Typography>
                ) : null}
              </FormItem>
            )}
          />
          {isError ? (
            <Alert theme="red">
              {match(error)
                .with({ status: 409 }, () => "This channel has already been added")
                .otherwise(() => "This channel can't be added. Please check and try again.")}
            </Alert>
          ) : null}
        </form>
        <RegisterChannelFlow.CTA
          type="submit"
          form="register-channel-url-form"
          loading={form.formState.isSubmitting || isPending}
          disabled={form.formState.isValid === false}
        >
          Continue
        </RegisterChannelFlow.CTA>
      </Form>
    </RegisterChannelFlow.Content>
  );
}

interface VerifyChannelProps {
  channel: { url: string; id: string };
  onSuccess: (channelId: string) => void;
}

function VerifyChannel({ channel, onSuccess }: VerifyChannelProps) {
  const workspaceId = useWorkspaceId();
  const isMobile = useIsMobile();
  const { error: errorToast, info } = useToastMessage();
  const {
    mutate: verifyChannel,
    isPending,
    isError,
  } = useVerifyYoutubeChannelApiV1({
    mutation: {
      onSuccess: ({ data }) => {
        if (data != null) {
          trackNewEvent(NewTrackingEvent.ClickVerifyButtonOfVerificationCodeModal, {
            verification_success: true,
          });
          onSuccess(data.item.channel.id);
        }
      },
      onError: () => {
        trackNewEvent(NewTrackingEvent.ClickVerifyButtonOfVerificationCodeModal, {
          verification_success: false,
        });
      },
    },
  });

  useEffect(() => {
    trackNewEvent(NewTrackingEvent.ViewVerificationCodeModal, {});
  }, []);

  return (
    <RegisterChannelFlow.Content>
      <div>
        <RegisterChannelFlow.BackButton className="mb-6" />
        <RegisterChannelFlow.Title className="mb-4">Verify that this is your YouTube channel</RegisterChannelFlow.Title>
        <Typography size="paragraphSm" color="textMuted" className="mb-6">
          Go to{" "}
          <Typography weight={700} asChild>
            <span>{isMobile ? "Channel settings > Description" : "Channel customization > Profile"}</span>
          </Typography>
          . Enter the verification code in the{" "}
          <Typography weight={700} asChild>
            <span>Description</span>
          </Typography>{" "}
          and click{" "}
          <Typography weight={700} asChild>
            <span>Publish</span>
          </Typography>
          .
        </Typography>
        <Label>Verification code</Label>
        <div className="relative mb-4">
          <Input placeholder="Verification code" value={YOUTUBE_CHANNEL_VERIFICATION_CODE} readOnly={true} />
          <button
            type="button"
            onClick={() => {
              navigator.clipboard
                .writeText(YOUTUBE_CHANNEL_VERIFICATION_CODE)
                .then(() => {
                  info("Verification code copied");
                })
                .catch(() => {
                  errorToast("Failed to copy verification code");
                });
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:text-textDefault transition-colors flex items-center justify-center"
          >
            <Icon name="copy" className="size-4" />
          </button>
        </div>
        <Typography size="xs" color="textPrimary" weight={600} asChild>
          <ExternalLink
            className={cn("block underline underline-offset-4 desktop:mb-4 mb-16", {
              "mb-4": isError,
            })}
            to={
              isMobile
                ? `${externalUrls.YOUTUBE_DEEPLINK_URL}/channel/${channel.id}/editing/profile`
                : `https://studio.youtube.com/channel/${channel.id}/editing/profile`
            }
          >
            Edit your profile
          </ExternalLink>
        </Typography>
        {isError ? <Alert theme="red">Verification failed. Please try again.</Alert> : null}
      </div>
      <RegisterChannelFlow.CTA
        onClick={() => verifyChannel({ data: { url: channel.url }, workspaceId })}
        loading={isPending ? "Verifying..." : undefined}
      >
        Verify
      </RegisterChannelFlow.CTA>
    </RegisterChannelFlow.Content>
  );
}
